import React, { useState, useEffect, useMemo } from 'react';
import { BaseReportTable, ReportFooter } from './BaseReportTable';
import { BaseColumn } from '@/components/common/table/BaseTable';
import { reportsApi, ReportParams } from '@/api/endpoints/reports';

interface UsersReportProps {
  dateFilter: string;
  customDateRange: { from: string; to: string };
}

export const UsersReport: React.FC<UsersReportProps> = ({ dateFilter, customDateRange }) => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [dateFilter, customDateRange]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: ReportParams = {
        dateType: dateFilter as any,
      };

      if (dateFilter === 'custom' && customDateRange.from && customDateRange.to) {
        params.fromDate = customDateRange.from;
        params.toDate = customDateRange.to;
      }

      const response = await reportsApi.getUsersReport(params);
      setReportData(response.data.data.users || []);
      setTotals(response.data.data.totals || {});
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define columns with proper alignment and widths
  const columns: BaseColumn[] = useMemo(() => [
    { 
      key: "userName", 
      label: "USER", 
      sortable: true, 
      visible: true,
      width: "200px",
      minWidth: "200px",
      render: (user) => user.userName || 'N/A'
    },
    { 
      key: "totalTasks", 
      label: "TOTAL TASKS", 
      sortable: true, 
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (user) => user.totalTasks || 0
    },
    { 
      key: "totalSubTasks", 
      label: "TOTAL SUB-TASKS", 
      sortable: true, 
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (user) => user.totalSubTasks || 0
    },
    { 
      key: "totalEstimatedTime", 
      label: "TOTAL EST. TIME", 
      sortable: true, 
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (user) => user.totalEstimatedTime || 0
    },
    { 
      key: "totalActualTime", 
      label: "TOTAL TIME TAKEN", 
      sortable: true, 
      visible: true,
      width: "80px",
      minWidth: "80px",
      render: (user) => user.totalActualTime || 0
    },
  ], []);

  // Calculate totals for footer
  const footerTotals = useMemo(() => ({
    count: reportData.length,
    totalTasks: reportData.reduce((sum, user) => sum + (user.totalTasks || 0), 0),
    totalSubTasks: reportData.reduce((sum, user) => sum + (user.totalSubTasks || 0), 0),
    totalEstimatedTime: reportData.reduce((sum, user) => sum + (user.totalEstimatedTime || 0), 0),
    totalActualTime: reportData.reduce((sum, user) => sum + (user.totalActualTime || 0), 0),
  }), [reportData]);

  // Column config for footer alignment
  const columnConfig = useMemo(() => 
    columns.map(col => ({
      key: col.key,
      width: col.width || '100px',
      minWidth: col.minWidth || '100px',
      label: col.label
    }))
  , [columns]);

  return (
    <div className="h-full bg-white flex flex-col">
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full">
          <BaseReportTable
            data={reportData}
            columns={columns}
            loading={loading}
            emptyMessage="No user data found"
            selectable={false}
            showFilters={false}
            sortable={true}
            onSort={(columnKey) => console.log("Sort by:", columnKey)}
            dropdownConfig={{}}
            selectedFilters={{}}
            getItemId={(user) =>
              user._id || user.userId || `user-${Math.random()}`
            }
            containerClassName="h-full"
            rowClassName="border-b border-gray-100 hover:bg-gray-50"
          />
        </div>
      </div>

      <ReportFooter 
        data={reportData}
        columnConfig={columnConfig}
        totals={footerTotals}
      />
    </div>
  );
};